
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { ContactInfo } from "@/types";
import FormField from "./FormField";

interface ContactFormProps {
  onSubmitSuccess: (info: ContactInfo) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSubmitSuccess }) => {
  const [formData, setFormData] = useState<ContactInfo>({
    firstName: "",
    lastName: "",
    company: "",
    role: "",
    title: "",
    email: "",
    committees: "",
    phone: "",
    consent: false
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs separately
    const inputValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: inputValue
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const requiredFields: Array<keyof ContactInfo> = [
      'firstName', 'lastName', 'email', 'company', 'role', 'title', 'committees'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Please check your input",
        description: "Some required fields are missing or invalid.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Call the success handler with the form data
      onSubmitSuccess(formData);
      
      toast({
        title: "Information submitted",
        description: "We'll process your report shortly",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Submission failed",
        description: "There was a problem sending your information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
      <h3 className="text-xl font-semibold mb-4">Request Your Full Report</h3>
      <p className="text-gray-600 mb-6">
        Fill out the form below to receive a detailed report with actionable recommendations.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="firstName"
            name="firstName"
            label="First Name"
            placeholder="John"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
          />
          
          <FormField
            id="lastName"
            name="lastName"
            label="Last Name"
            placeholder="Doe"
            value={formData.lastName}
            onChange={handleChange}
            error={errors.lastName}
          />
        </div>
        
        <FormField
          id="email"
          name="email"
          label="Email Address"
          placeholder="johndoe@company.com"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
        />
        
        <FormField
          id="company"
          name="company"
          label="Company"
          placeholder="Acme Corporation"
          value={formData.company}
          onChange={handleChange}
          error={errors.company}
        />
        
        <FormField
          id="role"
          name="role"
          label="Your Position"
          placeholder="CEO, Director, etc."
          value={formData.role}
          onChange={handleChange}
          error={errors.role}
        />
        
        <FormField
          id="title"
          name="title"
          label="Your Role"
          placeholder="Board Member, Director, etc."
          value={formData.title}
          onChange={handleChange}
          error={errors.title}
        />
        
        <FormField
          id="committees"
          name="committees"
          label="Board Committees"
          placeholder="Audit, Risk, Technology, etc."
          value={formData.committees}
          onChange={handleChange}
          error={errors.committees}
        />
        
        <div className="pt-2">
          <Button 
            type="submit" 
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Get Your Report"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
